import React from "react";
import projectMapStyle from "./styles/ProjectMap.module.scss"

import throttle from "lodash/throttle";
import Markdown from "react-markdown";
import FBContent from "../components/templates/FBContent"
import mapDetailsLabels from "../assets/project-assets/mapDetailsLabels";
/* graphics */
import { ReactComponent as ProcessElement } from "../assets/project-assets/project-steps/process-element.svg";
import ProcessBankGraphics from "../assets/project-assets/project-steps/process-bank.svg";
import ProcessSoldGraphics from "../assets/project-assets/project-steps/process-sold.svg";
import ProcessInvestorGraphics from "../assets/project-assets/project-steps/process-investor.svg";
import ProcessDeveloperGraphics from "../assets/project-assets/project-steps/process-developer.svg";

import mapTileDeveloper from "../assets/project-assets/markdown-icons/map-tile-developer.svg";
import mapTileProjectCompany from "../assets/project-assets/markdown-icons/map-tile-projectcompany.svg";
import mapTileVault from "../assets/project-assets/markdown-icons/map-tile-vault.svg";
import mapTileInvestor from "../assets/project-assets/markdown-icons/map-tile-investor.svg";
import mapTileBank from "../assets/project-assets/markdown-icons/map-tile-bank.svg";
import mapTileProject from "../assets/project-assets/markdown-icons/map-tile-project.svg";
import mapTileReview from "../assets/project-assets/markdown-icons/map-tile-review.svg";
import mapTileSold from "../assets/project-assets/markdown-icons/map-tile-sold.svg";
import mapTileFB from "../assets/project-assets/markdown-icons/map-tile-fundbricks.svg";


import WhatsInItForMe from "../components/molecules/WhatsInItForMe"
import ProjectContentTable from "../components/molecules/ProjectContentTable"
import DocumentsElement from "../components/molecules/DocumentsElement"
import { TranslatedTerms } from "../globals/TranslatedTerms";
import { lang } from '../globals/Translations';

const stepImages = {
    step1: mapTileDeveloper,
    step2: mapTileProjectCompany,
    step3: mapTileVault,
    step4: mapTileInvestor,
    step5: mapTileBank,
    step6: mapTileFB,
    step7: mapTileProject,
    step8: mapTileReview,
    step9: mapTileSold,
};



const tabHeight = 69;
const hashName = "step_content_";

const doScroll = id => {
    window.scrollTo({
        top:
            document.getElementById(id).getBoundingClientRect().top +
            window.pageYOffset -
            tabHeight -
            20,
        behavior: "smooth",
    });
};

const noop = () => { };

function useThrottledOnScroll(callback, delay) {
    const throttledCallback = React.useMemo(
        () => (callback ? throttle(callback, delay) : noop),
        [callback, delay]
    );

    React.useEffect(() => {
        if (throttledCallback === noop) return undefined;

        window.addEventListener("scroll", throttledCallback);
        return () => {
            window.removeEventListener("scroll", throttledCallback);
            throttledCallback.cancel();
        };
    }, [throttledCallback]);
}

export default function ProjectMap(props) {
    const localizedContent = props.project && props.project.localizedContent || {}
    const investmentDescription = localizedContent.investmentDescription || ""
    const developerDescription = localizedContent.developerDescription || ""
    const projectCompanyDescription = localizedContent.projectCompanyDescription || ""
    const vaultDescription = localizedContent.vaultDescription || ""
    const crowdinvestorDescription = localizedContent.crowdinvestorDescription || ""
    const bankDescription = localizedContent.bankDescription || ""
    const fundbricksFeeDescription = localizedContent.fundbricksFeeDescription || ""
    const projectDescription = localizedContent.projectDescription || ""
    const buildReviewDescription = localizedContent.buildReviewDescription || ""
    const sellingPeriodDescription = localizedContent.sellingPeriodDescription || ""
    const paybackDescription = localizedContent.paybackDescription || ""
    const paybackBankDescription = localizedContent.paybackBankDescription || ""
    const paybackCrowdInvestoorDescription = localizedContent.paybackCrowdInvestoorDescription || ""
    const paybackDeveloperDescription = localizedContent.paybackDeveloperDescription || ""
    const riskDescription = localizedContent.riskDescription || ""

    const [activeState, setActiveState] = React.useState(null);

    let stepContents = [
        {
            stepNo: 0,
            title: mapDetailsLabels.investment.title,
            label: mapDetailsLabels.investment.label,
            text: investmentDescription,
            children: (
                <div className={projectMapStyle.WhatsInItForMeContainer}>
                    <WhatsInItForMe
                        project={props.project}
                    />
                </div>
            ),
        },
        {
            stepNo: 0,
            title: lang(TranslatedTerms.DOCUMENTS),
            label: lang(TranslatedTerms.INVESTMENT_PROJECT_LABEL_DOCUMENT),
            children: (
                <DocumentsElement project={props.project}/>
            ),
        },
        {
            stepNo: 1,
            title: mapDetailsLabels.developer.title,
            label: mapDetailsLabels.developer.label,
            text: developerDescription,
        },
        {
            stepNo: 2,
            title: mapDetailsLabels.projectcompany.title,
            label: mapDetailsLabels.projectcompany.label,
            text: projectCompanyDescription,
        },
        {
            stepNo: 3,
            title: mapDetailsLabels.vault.title,
            label: mapDetailsLabels.vault.label,
            text: vaultDescription,
        },
        {
            stepNo: 4,
            title: mapDetailsLabels.crowdinvestor.title,
            label: mapDetailsLabels.crowdinvestor.label,
            text: crowdinvestorDescription,
        },
        {
            stepNo: 5,
            title: mapDetailsLabels.bank.title,
            label: mapDetailsLabels.bank.label,
            text: bankDescription,
        },
        {
            stepNo: 6,
            title: mapDetailsLabels.fb.title,
            label: mapDetailsLabels.fb.label,
            text: fundbricksFeeDescription,
        },
        {
            stepNo: 7,
            title: mapDetailsLabels.project.title,
            label: mapDetailsLabels.project.label,
            text: projectDescription,
            attachments: props.project && props.project.images,
        },
        {
            stepNo: 8,
            title: mapDetailsLabels.review.title,
            label: mapDetailsLabels.review.label,
            text: buildReviewDescription,
        },
        {
            stepNo: 9,
            title: mapDetailsLabels.sold.title,
            label: mapDetailsLabels.sold.label,
            text: sellingPeriodDescription,
        },
        {
            stepNo: 10,
            title: mapDetailsLabels.payback.title,
            label: mapDetailsLabels.payback.label,
            text: paybackDescription,
            children: (
                <img
                    src={ProcessSoldGraphics}
                    className={projectMapStyle.stepProcessGraphics}
                    alt=""
                />
            ),
        },
        {
            stepNo: 11,
            title: mapDetailsLabels.bankreturn.title,
            label: mapDetailsLabels.bankreturn.label,
            text: paybackBankDescription,
            children: (
                <img
                    src={ProcessBankGraphics}
                    className={projectMapStyle.stepProcessGraphics}
                    alt=""
                />
            ),
        },
        {
            stepNo: 12,
            title: mapDetailsLabels.crowdinvestorreturn.title,
            label: mapDetailsLabels.crowdinvestorreturn.label,
            text: paybackCrowdInvestoorDescription,
            children: (
                <img
                    src={ProcessInvestorGraphics}
                    className={projectMapStyle.stepProcessGraphics}
                    alt=""
                />
            ),
        },
        {
            stepNo: 13,
            title: mapDetailsLabels.developerreturn.title,
            label: mapDetailsLabels.developerreturn.label,
            text: paybackDeveloperDescription,
            children: (
                <img
                    src={ProcessDeveloperGraphics}
                    className={projectMapStyle.stepProcessGraphics}
                    alt=""
                />
            ),
        },
        {
            stepNo: 14,
            title: mapDetailsLabels.economy.title,
            label: mapDetailsLabels.economy.label,
            text: "",
            children: <ProjectContentTable table={localizedContent.economy}/>
        },
        {
            stepNo: 15,
            title: mapDetailsLabels.risks.title,
            label: mapDetailsLabels.risks.label,
            text: riskDescription,
        },
    ];

    // append steps with hash and article node id
    let itemsServer = stepContents.map(tab => {
        return {
            ...tab,
            hash: tab.stepNo,
            node: document.getElementById(`${hashName}${tab.stepNo}`),
        };
    });

    const itemsClientRef = React.useRef([]);
    React.useEffect(() => {
        itemsClientRef.current = itemsServer;
    }, [itemsServer]);

    const clickedRef = React.useRef(false);
    const unsetClickedRef = React.useRef(null);

    // function to get currently active article (based on scroll height)
    const findActiveIndex = React.useCallback(() => {
        // set default if activeState is null
        if (activeState === null) setActiveState(itemsServer[0].hash);

        // Don't set the active index based on scroll if a link was just clicked
        if (clickedRef.current) return;

        let active;
        for (let i = itemsClientRef.current.length - 1; i >= 0; i -= 1) {
            // No hash if we're near the top of the page
            if (document.documentElement.scrollTop < 0) {
                active = { hash: null };
                break;
            }

            const item = itemsClientRef.current[i];

            if (
                item.node &&
                item.node.offsetTop <
                document.documentElement.scrollTop +
                document.documentElement.clientHeight / 2 +
                tabHeight
            ) {
                active = item;
                break;
            }
        }

        if (active && activeState !== active.hash) {
            setActiveState(active.hash);
        }
    }, [activeState, itemsServer]);

    // Corresponds to 10 frames at 60 Hz
    useThrottledOnScroll(itemsServer.length > 0 ? findActiveIndex : null, 166);

    const handleMapClick = event => {
        // Used to disable findActiveIndex if the page scrolls due to a click
        clickedRef.current = true;
        unsetClickedRef.current = setTimeout(() => {
            clickedRef.current = false;
        }, 1000);

        try {
            const parent = event.target.closest('g[id^="T"]') || null;
            const id = Number(parent.getAttribute("id").substring(1, 3));
            if (activeState !== id) {
                setActiveState(id);
                doScroll(`${hashName}${id}`);
            }
        } catch (e) {
            //console.error(e);
            return;
        }
    };

    React.useEffect(
        () => () => {
            clearTimeout(unsetClickedRef.current);
        },
        []
    );

    const backToProjectMap = event => {
        if (event) event.stopPropagation();
        doScroll("projectMap");
    };

    const newClassNames = React.useMemo(() => {
        return `${activeState === 0 ? projectMapStyle["step0"] : ""}
            ${Array.from(
            { length: activeState },
            (v, k) => projectMapStyle["step" + (k + 1)]
        ).join(" ")}`;
    }, [activeState]);

    return (
        <div className={projectMapStyle.mapContainer}>
            <div
                className={projectMapStyle.mapContainerMobileButton}
                onClick={backToProjectMap}
            >
                <span>{lang(TranslatedTerms.SEE_PROJECT_DETAILS)}</span>
            </div>
            <ProcessElement
                className={`${projectMapStyle.projectMap} ${newClassNames}`}
                onClick={handleMapClick}
                id="projectMap"
            />
            <div className={projectMapStyle.projectDescription}>
                {stepContents.map((content, index) => {
                    return (
                        <div
                            key={index}
                            id={`${hashName}${content.stepNo}`}
                            className={`${projectMapStyle.normalArticle}
                    ${activeState === content.stepNo
                                    ? projectMapStyle.activeArticle
                                    : ""
                                }`}
                        >
                            {stepImages[`step${content.stepNo}`] && (
                                <img
                                    src={stepImages[`step${content.stepNo}`]}
                                    className={projectMapStyle.articleImage}
                                    alt=""
                                />
                            )}
                            <h2 className={projectMapStyle.articleHeader}>
                                {content.title}
                            </h2>
                            <div className={projectMapStyle.articleLabel}>
                                {content.label && (
                                    <Markdown children={content.label} />
                                )}
                            </div>
                            <div className={projectMapStyle.articleContent}>
                                {content.text && content.text !== "n/a" && (
                                    <FBContent content={content.text} contentType={props.project.localizedContent.contentType} />
                                )}
                                {content.children && content.children}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
